import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Stack, Button } from '@mui/material';
import ImageProcessingVisual from './ImageProcessingVisual';
import {
  CARD_INSET_HORIZONTAL_PX,
  CARD_INSET_TOP_PX,
  CONTENT_BLOCK_HEIGHT,
  externalFlowContentColumnSx,
  externalFlowGlassCardLayoutSx,
  externalFlowGlassSurfaceSx,
  externalFlowInCardBrandingSx,
  externalFlowLineClamp3Sx,
  ICON_SMALL,
  LABEL_SECONDARY,
  SUBTITLE_FONT_SIZE,
  UPLOAD_FLOW_PAGE_BG,
  externalFlowSecondaryTextButtonSx,
} from './uploadCardLayout';
import { useAnalytics } from '../../utils/eventLogAnalytics';

// Shortened preview height (10px less than the original flex-1 result of ~144px).
const IMAGE_HEIGHT_PX = 134;

const BarSpinner = () => {
  return (
    <Box
      sx={{
        position: 'relative',
        width: ICON_SMALL,
        height: ICON_SMALL,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {[...Array(12)].map((_, index) => (
        <Box
          key={index}
          sx={{
            position: 'absolute',
            width: 4,
            height: 12,
            borderRadius: 2,
            bgcolor: 'rgba(0, 0, 0, 0.35)',
            transform: `rotate(${index * 30}deg) translateY(-14px)`,
            animation: `barFade 1.2s linear infinite`,
            animationDelay: `${-1.2 + index * 0.1}s`,
            '@keyframes barFade': {
              '0%': { opacity: 1 },
              '100%': { opacity: 0.2 },
            },
          }}
        />
      ))}
    </Box>
  );
};

const CYCLING_MESSAGES = [
  { delay: 0,     text: 'Analyzing...' },
  { delay: 1500,  text: 'Identifying rooms...' },
  { delay: 3000,  text: 'Reading dimensions...' },
  { delay: 4500,  text: 'Placing furniture to scale...' },
  { delay: 6000,  text: 'Trying different layouts...' },
  { delay: 8000,  text: 'Building your interactive layout...' },
  { delay: 11000, text: 'Shuffle until you like what you see...' },
  { delay: 16000, text: 'Complex plans take a moment...' },
  { delay: 22000, text: 'Still working...' },
  { delay: 30000, text: 'Nearly there...' },
];

const UploadingCard = ({ previewUrl, fileName, fileSize, onCancel, progress = 0 }) => {
  const [msgIndex, setMsgIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const timersRef = useRef([]);
  const { eventLog } = useAnalytics(null);

  useEffect(() => {
    if (msgIndex == 0) {
      eventLog(null, 'floor_plan_event', 'floorplan_uploaded', {
        fileName: fileName,
        fileSize: fileSize,
      });
    }
    timersRef.current = CYCLING_MESSAGES.slice(1).map((msg, i) =>
      setTimeout(() => {
        setVisible(false);
        setTimeout(() => {
          setMsgIndex(i + 1);
          setVisible(true);
        }, 400);
      }, msg.delay)
    );
    return () => timersRef.current.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Top inset used for the BarSpinner (no-preview) fallback layout
  const topInset = CARD_INSET_HORIZONTAL_PX - CARD_INSET_TOP_PX;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 0,
        flexShrink: 0,
        bgcolor: UPLOAD_FLOW_PAGE_BG,
        width: '100%',
      }}
    >
      <Box
        sx={{
          ...externalFlowContentColumnSx,
          minHeight: CONTENT_BLOCK_HEIGHT,
          maxHeight: CONTENT_BLOCK_HEIGHT,
        }}
      >
        <Box
          sx={{
            ...externalFlowGlassCardLayoutSx,
            ...externalFlowGlassSurfaceSx,
          }}
        >
          <Stack
            spacing={0}
            alignItems="center"
            sx={{
              width: '100%',
              height: '100%',
              pt: `${topInset}px`,
              pb: 0,
            }}
          >
            {/*
              Image preview (fixed height, 16px inset on all sides via card px + stack pt)
              or BarSpinner (flex-1 to fill and center). Opacity softened to 0.7.
            */}
            <Box
              sx={{
                width: '100%',
                height: previewUrl ? IMAGE_HEIGHT_PX : undefined,
                flex: previewUrl ? undefined : 1,
                flexShrink: 0,
                minHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {previewUrl ? (
                <Box sx={{ width: '100%', height: '100%', opacity: 0.7 }}>
                  <ImageProcessingVisual previewUrl={previewUrl} progress={progress} />
                </Box>
              ) : (
                <BarSpinner />
              )}
            </Box>

            {/* Cycling status messages */}
            <Typography
              variant="body1"
              sx={{
                color: visible ? LABEL_SECONDARY : 'rgba(0,0,0,0.15)',
                fontSize: SUBTITLE_FONT_SIZE,
                lineHeight: 1.4,
                mt: '6px',
                textAlign: 'center',
                opacity: visible ? 1 : 0.3,
                transition: 'color 400ms ease-in-out, opacity 400ms ease-in-out',
                flexShrink: 0,
                ...externalFlowLineClamp3Sx,
              }}
            >
              {CYCLING_MESSAGES[msgIndex].text}
            </Typography>

            {/* Flex spacer — pushes Cancel toward vertical center between text and branding */}
            <Box sx={{ flex: 1, minHeight: 0 }} />

            {/* Cancel Button */}
            {onCancel && (
              <Button onClick={onCancel} sx={externalFlowSecondaryTextButtonSx}>
                Cancel
              </Button>
            )}

            {/* Flex spacer — balances Cancel between cycling text and branding */}
            <Box sx={{ flex: 1, minHeight: 0 }} />

            {/* In-card branding */}
            <Typography sx={externalFlowInCardBrandingSx}>
              Powered by MagicFurnish
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default UploadingCard;
